import { ColDef } from 'ag-grid-enterprise';
import { alignLeft, alignRight, component, mergeClasses } from './utils';
import { formatPrettyNumber } from '@/modules/common/components/pretty-number';
import * as cols from './common';
import { h } from 'vue';
import { VBtn, VChip, VIcon, VTooltip } from 'vuetify/lib';
import i18n from '@/localisation/i18n';
import { getEventTypeColor, timeInForceAbbr } from '@/modules/marketplace/helpers/marketplace';
import { agreementPopoverAdapter, rateAdapter, userIdentifier } from './common';
import {
  OmsHistoryExecutedItem,
  OmsHistoryItem,
  OmsOrderSummary,
} from '@/modules/marketplace/models';
export {} from './common';

interface Config {
  cellHighlight: (name: keyof OmsOrderSummary, index: number) => string;
}

export function eventTimestamp(): ColDef<OmsHistoryItem> {
  return {
    field: 'eventTimestamp',
    colId: 'eventTimestamp',
    headerName: 'When',
    sortable: true,
    cellDataType: 'date',
    ...mergeClasses([alignLeft(), { cellClass: 'highlight' }]),
  };
}

const eventTypeAdaptor = component<{
  item: OmsHistoryExecutedItem;
  openLoanDetails: (loanId: string | null) => void;
}>(
  (props) => () =>
    h('div', { style: { display: 'contents' } }, [
      h(
        VChip,
        {
          class: 'text-uppercase mr-2',
          props: { color: getEventTypeColor(props.item.eventType), xSmall: true },
        },
        [h('span', undefined, i18n.tc(`orderHistory.eventType.${props.item.eventType}`))]
      ),
      props.item.eventType === 'EXECUTED' && props.item.loanId
        ? h(
            VBtn,
            {
              class: 'icon-action',
              props: { icon: true, size: 24 },
              attrs: { 'data-test': 'open-loans-details' },
              on: { click: () => props.openLoanDetails(props.item.loanId) },
            },
            [h(VIcon, { props: { size: 24 } }, 'mdi-eye')]
          )
        : props.item.eventType === 'EXECUTED' && props.item.loanId === null
          ? h(
              VTooltip,
              {
                scopedSlots: {
                  activator: ({ on, attrs }) =>
                    h(VIcon, {
                      class: 'icon-action tooltip-icon',
                      attrs: {
                        'data-test': 'open-loans-details',
                        ...attrs,
                      },
                      props: { icon: true, size: 24 },
                      on,
                    }),
                },
                props: { bottom: true },
              },
              [h('span', undefined, 'Processing execution, details not available yet')]
            )
          : undefined,
    ])
);

export function eventType({
  openLoanDetails,
}: {
  openLoanDetails: (loanId: string | null) => void;
}): ColDef<OmsHistoryItem> {
  return {
    field: 'eventType',
    colId: 'eventType',
    sortable: false,
    headerName: 'Event',
    cellRendererSelector: (params) =>
      eventTypeAdaptor({
        item: params.data as OmsHistoryExecutedItem,
        openLoanDetails,
      }),
    ...alignLeft(),
  };
}

export function quantity(config: Config): ColDef<OmsHistoryItem> {
  const colQuantity = cols.quantity({
    field: 'summary.quantity',
    headerName: 'Order Qty.',
    sortable: false,
  });
  return {
    ...colQuantity,
    colId: 'quantity',
    ...mergeClasses([
      colQuantity,
      { cellClass: (params) => config.cellHighlight('quantity', params.rowIndex) },
    ]),
  } as ColDef<OmsHistoryItem>;
}

export function openQuantity(config: Config): ColDef<OmsHistoryItem> {
  return {
    field: 'summary',
    colId: 'openQuantity',
    sortable: false,
    headerName: 'Open Qty.',
    valueFormatter: (params) => formatPrettyNumber(params.value.quantity - params.value.filled),
    ...mergeClasses([
      alignLeft(),
      { cellClass: (params) => config.cellHighlight('openQuantity', params.rowIndex) },
    ]),
  };
}

export function filled(config: Config): ColDef<OmsHistoryItem> {
  return {
    field: 'summary.filled',
    colId: 'filled',
    sortable: false,
    headerName: 'Exec Qty.',
    valueFormatter: (params) => formatPrettyNumber(params.value),
    ...mergeClasses([
      alignLeft(),
      { cellClass: (params) => config.cellHighlight('filled', params.rowIndex) },
    ]),
  };
}

export function ioiQuantity(config: Config): ColDef<OmsHistoryItem> {
  return {
    field: 'summary.ioiQuantity',
    colId: 'openQuantity',
    sortable: false,
    headerName: 'IOI Qty.',
    valueFormatter: (params) => formatPrettyNumber(params.value),
    ...mergeClasses([
      alignLeft(),
      { cellClass: (params) => config.cellHighlight('ioiQuantity', params.rowIndex) },
    ]),
  };
}

export function firmQuantity(config: Config): ColDef<OmsHistoryItem> {
  return {
    field: 'summary.firmQuantity',
    colId: 'openQuantity',
    sortable: false,
    headerName: 'Firm Qty.',
    valueFormatter: (params) => formatPrettyNumber(params.value),
    ...mergeClasses([
      alignLeft(),
      { cellClass: (params) => config.cellHighlight('firmQuantity', params.rowIndex) },
    ]),
  };
}

export function fee(config: Config): ColDef<OmsHistoryItem> {
  return {
    field: 'summary.fee',
    colId: 'fee',
    sortable: false,
    headerName: 'Fee',
    valueFormatter: (params) => params.value,
    cellRendererSelector: (params) =>
      rateAdapter({
        rate: params.value,
        rateModifier: undefined,
        precision: undefined,
      }),
    ...mergeClasses([
      alignRight(),
      { cellClass: (params) => config.cellHighlight('fee', params.rowIndex) },
    ]),
  };
}

export function rebate(config: Config): ColDef<OmsHistoryItem> {
  return {
    field: 'summary.rebate',
    colId: 'rebate',
    sortable: false,
    headerName: 'Rebate Rate',
    valueFormatter: (params) => params.value,
    cellRendererSelector: (params) =>
      rateAdapter({
        rate: params.value,
        rateModifier: undefined,
        precision: undefined,
      }),
    ...mergeClasses([
      alignRight(),
      { cellClass: (params) => config.cellHighlight('rebate', params.rowIndex) },
    ]),
  };
}

export function avgExecutionFee(config: Config): ColDef<OmsHistoryItem> {
  return {
    field: 'summary.avgExecutionFee',
    colId: 'avgExecutionFee',
    sortable: false,
    headerName: 'Avg Exec Fee',
    valueFormatter: (params) => params.value,
    cellRendererSelector: (params) =>
      rateAdapter({
        rate: params.value,
        rateModifier: undefined,
        precision: undefined,
      }),
    ...mergeClasses([
      alignRight(),
      { cellClass: (params) => config.cellHighlight('avgExecutionFee', params.rowIndex) },
    ]),
  };
}

export function avgExecutionRebate(config: Config): ColDef<OmsHistoryItem> {
  return {
    field: 'summary.avgExecutionRebate',
    colId: 'avgExecutionRebate',
    sortable: false,
    headerName: 'Avg Exec Rebate',
    valueFormatter: (params) => params.value,
    cellRendererSelector: (params) =>
      rateAdapter({
        rate: params.value,
        rateModifier: undefined,
        precision: undefined,
      }),
    ...mergeClasses([
      alignRight(),
      { cellClass: (params) => config.cellHighlight('avgExecutionRebate', params.rowIndex) },
    ]),
  };
}

export function agreements(config: Config): ColDef<OmsHistoryItem> {
  return {
    field: 'summary.agreements',
    colId: 'agreements',
    headerName: 'Agreements',
    valueFormatter: (params) => params.value,
    cellRendererSelector: (params) =>
      agreementPopoverAdapter({ agreementIds: params.value.map((a) => a.id) }),
    ...mergeClasses([
      alignLeft(),
      { cellClass: (params) => config.cellHighlight('agreements', params.rowIndex) },
    ]),
  };
}

export function minQuantity(config: Config): ColDef<OmsHistoryItem> {
  return {
    field: 'summary.minQuantity',
    colId: 'minQuantity',
    sortable: false,
    headerName: 'Min Qty.',
    valueFormatter: (params) => formatPrettyNumber(params.value),
    ...mergeClasses([
      alignLeft(),
      { cellClass: (params) => config.cellHighlight('minQuantity', params.rowIndex) },
    ]),
  };
}

const orderTypeAdapter = component<{
  orderType: string;
}>((props) => () => h('span', { class: 'text-capitalize' }, props.orderType.toLowerCase()));

export function orderType(config: Config): ColDef<OmsHistoryItem> {
  return {
    field: 'summary.orderType',
    colId: 'orderType',
    sortable: false,
    headerName: 'Type',
    cellRendererSelector: (params) => orderTypeAdapter({ orderType: params.value }),
    ...mergeClasses([
      alignLeft(),
      { cellClass: (params) => config.cellHighlight('orderType', params.rowIndex) },
    ]),
  };
}

export function timeInForceType(config: Config): ColDef<OmsHistoryItem> {
  return {
    field: 'summary.timeInForceType',
    colId: 'timeInForceType',
    sortable: false,
    headerName: 'Time In Force',
    valueFormatter: (params) => timeInForceAbbr(params.value),
    ...mergeClasses([
      alignLeft(),
      { cellClass: (params) => config.cellHighlight('timeInForceType', params.rowIndex) },
    ]),
  };
}

export function initiator(): ColDef<OmsHistoryItem> {
  return {
    field: 'initiator',
    colId: 'initiator',
    sortable: false,
    headerName: 'Initiator',
    ...userIdentifier(),
  };
}
